// Admin Portal — restore a client's configuration from an export.
//
// The destructive stage, so the screen is arranged around two separate
// consents rather than one. Planning is free and happens first. Applying is a
// deliberate second step. Deleting rows that exist now and are absent from the
// file is a third, because that is not restoring a backup — it is discarding
// whatever was done since it was taken.

import { useState } from "react";
import { AlertTriangle, Loader2, Upload, X } from "lucide-react";
import { authFetch } from "../../utils/api";

const Count = ({ label, n, tone }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>
    {n} {label}
  </span>
);

export const ClientRestoreModal = ({ client, onClose, onRestored }) => {
  const [exportData, setExportData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [allowDeletes, setAllowDeletes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const readFile = async (f) => {
    setError("");
    setPlan(null);
    try {
      const parsed = JSON.parse(await f.text());
      setExportData(parsed);
    } catch {
      setExportData(null);
      setError("That file is not valid JSON.");
    }
  };

  const call = async (body) => {
    setBusy(true);
    setError("");
    try {
      const res = await authFetch(`/admin/clients/${client.id}/restore`, {
        method: "POST",
        body: JSON.stringify({ export: exportData, ...body }),
      });
      if (res?.error) {
        setError(res.error);
        return null;
      }
      return res;
    } catch {
      setError("Network error — nothing was changed.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const runPlan = async () => {
    const res = await call({ apply: false, allow_deletes: allowDeletes });
    if (res) setPlan(res);
  };

  const runApply = async () => {
    const res = await call({ apply: true, allow_deletes: allowDeletes });
    if (res) {
      onRestored?.(res);
      onClose();
    }
  };

  const totals = plan?.totals || { add: 0, change: 0, delete: 0 };
  const tables = Object.entries(plan?.tables || {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Restore{" "}
            {client.name || client.client_name}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Export file
            </label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={(e) =>
                e.target.files?.[0] && readFile(e.target.files[0])
              }
              className="w-full text-sm"
            />
            {exportData?.exported_at && (
              <p className="mt-1 text-xs text-slate-500">
                Taken {new Date(exportData.exported_at).toLocaleString()}
                {exportData.exported_by ? ` by ${exportData.exported_by}` : ""}
              </p>
            )}
          </div>

          {error && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {plan && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Plan — nothing written
                </span>
                <Count
                  label="to add"
                  n={totals.add}
                  tone="bg-green-100 text-green-700"
                />
                <Count
                  label="to change"
                  n={totals.change}
                  tone="bg-amber-100 text-amber-700"
                />
                <Count
                  label="only here now"
                  n={totals.delete}
                  tone="bg-rose-100 text-rose-700"
                />
              </div>
              <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                {tables.length === 0 && (
                  <p className="text-sm text-slate-500">
                    Nothing differs — the live configuration already matches
                    this file.
                  </p>
                )}
                {tables.map(([table, work]) => (
                  <details key={table} className="text-sm">
                    <summary className="cursor-pointer text-slate-700">
                      {table}{" "}
                      <span className="text-xs text-slate-500">
                        +{work.counts.add} ~{work.counts.change} −
                        {work.counts.delete}
                      </span>
                    </summary>
                    <div className="mt-1 ml-4 space-y-1 text-xs">
                      {work.change.slice(0, 8).map((c, i) => (
                        <div key={i} className="text-slate-600">
                          <span className="text-slate-400">
                            {JSON.stringify(c.key)}
                          </span>
                          {Object.entries(c.columns)
                            .slice(0, 4)
                            .map(([col, v]) => (
                              <div key={col} className="ml-3">
                                {col}:{" "}
                                <span className="text-rose-600 line-through">
                                  {String(v.from ?? "—").slice(0, 40)}
                                </span>{" "}
                                <span className="text-green-700">
                                  {String(v.to ?? "—").slice(0, 40)}
                                </span>
                              </div>
                            ))}
                        </div>
                      ))}
                      {work.counts.change > 8 && (
                        <div className="text-slate-400">
                          …and {work.counts.change - 8} more
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
              {(plan.warnings || []).map((w, i) => (
                <p
                  key={i}
                  className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-200"
                >
                  {w}
                </p>
              ))}
            </div>
          )}

          {/* Third consent, and only offered once a plan shows there is
              something to delete — an unticked box for an impossible action is
              just an invitation to tick it. */}
          {plan && totals.delete > 0 && (
            <label className="flex items-start gap-2 px-3 py-2.5 bg-rose-50 border border-rose-200 rounded-lg text-sm">
              <input
                type="checkbox"
                checked={allowDeletes}
                onChange={(e) => {
                  setAllowDeletes(e.target.checked);
                  setPlan(null);
                }}
                className="mt-0.5 rounded border-rose-300"
              />
              <span className="text-rose-800">
                Also delete the {totals.delete} rows that exist now and are not
                in this file
                <span className="block text-xs text-rose-700 mt-0.5">
                  These were created after the backup was taken. Deleting them
                  discards that work. Leave unticked to restore the file's rows
                  without removing anything.
                </span>
              </span>
            </label>
          )}

          {!plan && exportData && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Planning reads only. Nothing is written until you review the
                plan and choose to apply it.
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-3 border-t border-slate-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={runPlan}
            disabled={busy || !exportData}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && !plan && <Loader2 className="w-4 h-4 animate-spin" />}
            {plan ? "Re-plan" : "Plan restore"}
          </button>
          <button
            onClick={runApply}
            disabled={busy || !plan || tables.length === 0}
            title={!plan ? "Plan first" : ""}
            className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && plan && <Loader2 className="w-4 h-4 animate-spin" />}
            Apply restore
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientRestoreModal;
